/**
 * A single app-wide slide-in detail drawer. Any panel can push content into it
 * via useDrawer().show({ title, body }) — used for row-level raw detail (a cache
 * entry, a logged request) without every panel owning its own drawer state.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { cx } from '../ui';

interface DrawerContent {
  title: ReactNode;
  body: ReactNode;
}
interface DrawerCtx {
  show: (c: DrawerContent) => void;
  close: () => void;
}

const Ctx = createContext<DrawerCtx | null>(null);

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<DrawerContent | null>(null);
  const show = useCallback((c: DrawerContent) => setContent(c), []);
  const close = useCallback(() => setContent(null), []);

  return (
    <Ctx.Provider value={{ show, close }}>
      {children}
      <aside className={cx('drawer', content && 'open')} aria-hidden={!content}>
        {content && (
          <>
            <div className="blk-head">
              <h3 className="text-[13px] font-semibold">{content.title}</h3>
              <button className="btn btn-sm ml-auto" onClick={close}>
                Close ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">{content.body}</div>
          </>
        )}
      </aside>
    </Ctx.Provider>
  );
}

export function useDrawer(): DrawerCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDrawer must be used within <DrawerProvider>');
  return c;
}
